import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ms from 'ms';

import {
  USER_ROLE_RENTER,
  USER_ROLE_ANONYMOUS,
} from 'common/userRoles';

import {
  isSunroomListing,
  getMarketSecurityDepositLabel,
} from 'common/listingHelpers';

import { SCREEN_ID_RENTER_PROFILE_PUBLIC } from 'client/constants/screenIds';
import withTransitionHook from 'client/helpers/withTransitionHook';
import allowedRoles from 'client/helpers/allowedRoles';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import branch from 'client/core/branch';

import HouseholdActions from 'client/actions/Household';
import HouseholdApplicationActions from 'client/actions/HouseholdApplication';
import UserActions from 'client/actions/User';

import SrRenterApplicationStatusCard from 'client/components/SrRenterApplicationStatusCard';
import SrRenterApplicationStatusCardSunroom from 'client/components/SrRenterApplicationStatusCardSunroom';
import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';

@withTransitionHook
@allowedRoles({
  roles: [
    USER_ROLE_RENTER,
    USER_ROLE_ANONYMOUS,
  ],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_RENTER_PROFILE_PUBLIC)
@branch(({ match }) => ({
  currentUser: ['currentUser'],
  userHouseholdApplications: ['household', 'applicationsByUserId', match.params.userId],
  appUrl: ['env', 'APP_URL'],
}))
@withScrollToTop
class RenterProfilePagePublic extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Profile | Sunroom Rentals',
    keywords: 'sunroom, profile',
  })

  static fetchData(match, inject) {
    const userId = Number(match.params.userId);
    if (userId) {
      return Promise.all([
        HouseholdActions.loadRenterProfile(inject, { userId }),
        HouseholdApplicationActions.getHouseholdApplicationsByUserId(inject, { userId }),
        UserActions.getCustomerSupportPhoneNumber(inject, { userId }),
      ]);
    }
  }

  static propTypes = {
    userHouseholdApplications: PropTypes.array,
  }

  componentDidMount() {
    this.refreshHouseholdApplicationsInterval = global.setInterval(
      this._refreshHouseholdApplications,
      ms('60s')
    );
  }

  componentWillUnmount() {
    if (this.refreshHouseholdApplicationsInterval) {
      global.clearInterval(this.refreshHouseholdApplicationsInterval);
    }
  }

  _refreshHouseholdApplications = () => {
    const { dispatch, match } = this.props;
    dispatch(HouseholdApplicationActions.getHouseholdApplicationsByUserId, {
      userId: Number(match.params.userId),
    });
  }

  render() {
    const {
      userHouseholdApplications,
      match,
    } = this.props;

    const matchingApps = userHouseholdApplications.filter((app) => {
      return app.householdListing.listingId === Number(match.params.listingId);
    }).length > 0;

    return (
      <SrBox padding='30px 10px 50px'>
        <SrBox margin='0 auto 30px auto'>
          <SrFlex justify='center'>
            <img alt='logo' src='/images/sunroom-logo.svg' />
          </SrFlex>
        </SrBox>
        {matchingApps && (
          <div className='SrRenterApplicationStatusCardList'>
            {userHouseholdApplications
              .filter((app) => {
                return app.householdListing.listingId === Number(match.params.listingId);
              })
              .map((app) => {
                const CardComponent = isSunroomListing(app.listing)
                  ? SrRenterApplicationStatusCardSunroom
                  : SrRenterApplicationStatusCard;
                return (
                  <CardComponent
                    key={`srascl-${app.listing.id}-${app.householdListing.householdId}`}
                    listing={app.listing}
                    partner={app.partner}
                    market={app.market}
                    household={app.household}
                    moveInDate={app.moveInDate}
                    householdListing={app.householdListing}
                    securityDepositStripeCharge={app.securityDepositStripeCharge}
                    securityDepositPaymentAccount={app.securityDepositPaymentAccount}
                    securityDepositLabel={getMarketSecurityDepositLabel(app)}
                    defaultPaymentAccount={app.defaultPaymentAccount}
                    householdUsers={app.householdUsers}
                    roommateApplications={app.roommateApplications}
                    guarantorApplications={app.guarantorApplications}
                    applicationSubmissions={app.applicationSubmissions}
                    householdApplicationsStatusUpdates={app.householdApplicationsStatusUpdates}
                    lease={app.lease}
                    viewingUserId={Number(match.params.userId)}
                  />
                );
              })}
          </div>
        )}
        {!matchingApps &&
          <SrBox margin='30vh auto'>
            <SrText
              align='center'
              font='greycliff'
              color='blueGreycliff'
              fontSize={35}
              lineHeight='45px'
              display='block'
            >
              Application not available
            </SrText>
          </SrBox>
        }
      </SrBox>
    );
  }
}

RenterProfilePagePublic.displayName = 'RenterProfilePagePublic';

export default RenterProfilePagePublic;
